import { addMessage, addMessageAdvanced } from "~/features/toasts/redux/actions";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import {
  loadWishlistDetailsPostProcessAction,
  loadWishlistNamesStartAction,
  loadWishlistNamesSuccessAction,
  saveCommentAction,
  selectWishlistAction,
  setAddAllClicked,
} from "~/features/wishlist/redux/actions";
import { COPY_WISHLIST_STATUS_PARAM, REMOVED_ITEMS } from "~/features/wishlist/redux/actionTypes";
import type { RootState } from "~/app/store";
import type { ActionType, AnyAction } from "~/app/store/types";
import type { ThunkAction } from "redux-thunk";
import { fetchWishlists, type FetchWishlistsResponse } from "~/features/wishlist/api/fetchWishlists";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { TranslationType } from "~/common/types/TranslationModel";
import { requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { CART_PNA_THRESHOLD } from "~/utils/constants";
import { sendData as setWishlistComment } from "~/features/wishlist/api/SetWishlistComment";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function wishlistPageMounted(): ThunkResult<void> {
  return (dispatch, getState) => {
    const params = new URLSearchParams(window.location.search);
    const copyWishlistStatus = params.get(COPY_WISHLIST_STATUS_PARAM);
    if (copyWishlistStatus !== null) {
      if (copyWishlistStatus === "true") {
        dispatch(addMessage("info", "page.wishlist.copyWishlist.success", "translation"));
      } else {
        dispatch(addMessage("error", "page.wishlist.copyWishlist.error", "translation"));
      }
    }

    const copyWishlistRemovedItems = params.get(REMOVED_ITEMS);
    if (copyWishlistRemovedItems !== null) {
      if (copyWishlistRemovedItems === "true") {
        dispatch(addMessage("info", "page.wishlist.copyWishlist.hasRemovedItems", "translation"));
      }
    }

    dispatch(fetchWishlistNames()).then(() => {
      const innerState = getState().wishlistPage;
      const wishlistId = innerState.wishlistIds[0];
      dispatch(selectWishlistThunk(wishlistId));
    });
    dispatch(loadCartAmounts());
  };
}

let fetchNamesPromise: Promise<FetchWishlistsResponse> | null = null;

export function fetchWishlistNames(force?: boolean): ThunkResult<Promise<FetchWishlistsResponse>> {
  return (dispatch, getState) => {
    if (!userIsLoggedIn()) {
      return Promise.resolve({ products: [], wishlistEntries: [], wishlists: [] });
    }
    const state = getState().wishlistPage;
    const shouldFetch = force || (!state.loading && !state.loaded);
    if (shouldFetch) {
      dispatch(loadWishlistNamesStartAction());
      fetchNamesPromise = fetchWishlists();
      fetchNamesPromise.then(() => (fetchNamesPromise = null));

      return fetchNamesPromise.then((data) => {
        dispatch(loadWishlistNamesSuccessAction(data));
        return data;
      });
    } else {
      return fetchNamesPromise || Promise.resolve({ products: [], wishlistEntries: [], wishlists: [] });
    }
  };
}

export function selectWishlistThunk(wishlistId?: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    dispatch(setAddAllClicked(false));

    if (!wishlistId) {
      return Promise.resolve(dispatch(selectWishlistAction(wishlistId)));
    }

    const state = getState().wishlistPage;

    // TODO: TEMP
    if (!(state.wishlists[wishlistId]?.processed ?? false)) {
      dispatch(selectWishlistAction(wishlistId));
      return dispatch(fetchWishlistDetails(wishlistId));
    } else {
      return Promise.resolve(dispatch(selectWishlistAction(wishlistId)));
    }
  };
}
export function fetchWishlistDetails(wishlistId: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState().wishlistPage;
    const wishlist = state.wishlists[wishlistId];

    // TODO: TEMP
    if (!wishlist || wishlist.processed) {
      return Promise.resolve({});
    }

    dispatch(loadWishlistDetailsPostProcessAction(wishlistId));

    if (wishlist.entryIds.length < CART_PNA_THRESHOLD) {
      const entries = wishlist.entryIds.map((id) => state.entries[id]);

      const allPromises: Promise<unknown>[] = entries
        .map((entry) => {
          const aPromise = dispatch(requestAvailabilityData(entry.productCode));
          const pPromise = dispatch(requestPriceData(entry.productCode, entry.priceReference));

          return [aPromise, pPromise];
        })
        .flat();

      return Promise.all(allPromises).then(() => dispatch(setAddAllClicked(false)));
    } else {
      dispatch(
        addMessageAdvanced("info", {
          type: TranslationType.INTERNAL,
          value: "page.wishlist.message.abovePnaThreshold",
          params: { count: CART_PNA_THRESHOLD },
        }),
      );
      return Promise.resolve({});
    }
  };
}

export function fetchWishDetail(productCode: string, priceReference: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const availability = dispatch(requestAvailabilityData(productCode, true));
    const price = dispatch(requestPriceData(productCode, priceReference, true));
    return Promise.all([availability, price]);
  };
}

export function updateComment(comment: string): ThunkResult<
  Promise<
    ActionType<{
      wishlistId: string;
      comment: string;
    }>
  >
> {
  return (dispatch, getState) => {
    const state = getState().wishlistPage;
    const wishlistId = state.selectedWishlistId!;
    const wishlist = state.wishlists[wishlistId];
    const wishlistName = wishlist.name;

    return setWishlistComment({
      wishlistName,
      id: wishlistId,
      newComment: comment,
    }).then(() => dispatch(saveCommentAction(wishlistId, comment)));
  };
}
